import {
  choferPartialBuilder,
  ChoferPartialModel
} from 'app/chofer/models/Chofer';
import {
  applicantBuilder,
  ApplicantCrud,
  ApplicantModel
} from 'app/client/clientBusiness/Applicant/models/ApplicantModel';
import {
  authorizedBuilder,
  AuthorizedModel,
  AuthorizedModelCRUD
} from 'app/client/clientBusiness/Authorized/models/Authorized';
import { RateModel } from 'app/client/clientBusiness/rates/models/RateModel';
import {
  domicilioBuilder,
  DomicilioModel
} from 'app/domicilio/models/DomicilioModel';
import {
  ViajeCanalesDeIngreso,
  ViajeTrasladoEstado,
  ViajeTrasladoTipoServicio
} from 'app/viaje/models';
import moment, { Moment } from 'moment';
import { TransferReservationPrices } from './TransferReservationPrices';
import { AirportDetails } from './TransferReservationPartial';

export interface BusRequirement {
  provider_price: number;
  customer_price: number;
  is_outsourced: boolean;
  observation: string;
  bus_type_id: number;
  bus_type?: { id: number; name: string; amount_passengers: number };
  vehicle?: any;
  vehicle_id?: number | null;
}

export interface ViajeTrasladoModelLegacyDontUse {
  id: number;
  cliente: { id: number; name: string };
  canalIngreso: ViajeCanalesDeIngreso;
  plataformaId: string | null;
  prioridad: boolean;
  tipoServicio: string;
  fixedRate: RateModel['categories'][0]['fixedPrices'][0] | null;
  hasOutSite: boolean;
  solicitante: ApplicantModel;
  puntos: ViajeTrasladoPunto[];
  fechaYHoraDeSalida: Moment;
  fechaYHoraDeLlegada: Moment;
  observacionCoordinacion: string | null;
  estado: ViajeTrasladoEstado;
  state_datetime: Moment;
  choferPreAsignado: ChoferPartialModel | null;
  choferAsignado: ChoferPartialModel | null;
  autorizado?: AuthorizedModel;
  costCenter?: string;
  fixedRateName?: string;
  subCostCenter?: string;
  sector?: string;
  buyOrder?: string;
  employeeNumber?: string;
  platformApplication?: string;
  tripNumber?: string;
  data1: string;
  data2: string;
  data3: string;
  deviation_observation: string | null;
  arrival_notification_channel: string | null;
  estimated_prices: TransferReservationPrices;
  calculated_prices?: TransferReservationPrices;
  final_prices?: TransferReservationPrices;
  pay_with_cash: boolean;
  lltpais: boolean;
  customer_price?: number;
  provider_price?: number;
  bus_requirement?: BusRequirement | null;
  reason_reserve_past?: string | null;
  airport_details?: AirportDetails | null;
  general_observation?: string | null;
  event?: { id: number; name: string } | null;
  is_event?: boolean;
  event_name?: string | null;
}

export type ViajeTrasladoCreateModelLegacyDontUse = Omit<
  ViajeTrasladoModelLegacyDontUse,
  | 'id'
  | 'choferPreAsignado'
  | 'choferAsignado'
  | 'estado'
  | 'solicitante'
  | 'autorizado'
  | 'fixedRateName'
  | 'state_datetime'
  | 'deviation_observation'
  | 'arrival_notification_channel'
  | 'estimated_prices'
  | 'calculated_prices'
  | ''
> & {
  solicitante: ApplicantCrud;
  autorizado?: AuthorizedModelCRUD;
  general_observation?: string | null;
  reason_reserve_past?: string | null;
  airport_details?: AirportDetails | null;
  event_id?: number | null;
};

export interface PasajeroTrasladoModel {
  nombreCompleto: string;
  telefono: number | string;
  mail: string;
}

export interface ViajeTrasladoPunto {
  domicilio: DomicilioModel;
  pasajeros: PasajeroTrasladoModel[];
  observacion: string | null;
  minutosEspera: number | null;
}

export const passangerBuilder = {
  toBackEnd: (pasajero: PasajeroTrasladoModel) => ({
    name: pasajero.nombreCompleto,
    phone: pasajero.telefono === 0 ? null : pasajero.telefono,
    email: pasajero.mail
  }),
  fromBackEnd: (pasajero): PasajeroTrasladoModel => ({
    nombreCompleto: pasajero.name,
    telefono: pasajero.phone,
    mail: pasajero.email
  })
};

export const locationBuilder = {
  toBackEnd: (point: ViajeTrasladoPunto) => ({
    address: domicilioBuilder.toBackEnd(point.domicilio),
    observation: point.observacion,
    minutes_waiting: point.minutosEspera,
    passengers: point.pasajeros.map(passangerBuilder.toBackEnd)
  }),
  fromBackEnd: (l): ViajeTrasladoPunto => ({
    domicilio: domicilioBuilder.fromBackEnd(l.address),
    pasajeros: l.passengers.map(passangerBuilder.fromBackEnd),
    observacion: l.observation,
    minutosEspera: l.minutesWaiting
  })
};

export const viajeTrasladoBuilderLegacyDontUse = {
  toBackEnd: (viaje: ViajeTrasladoCreateModelLegacyDontUse) => ({
    //Si es LLT-pais se agregan los campos
    ...(viaje.lltpais
      ? {
        is_llt_pais: true,
        provider_price: viaje.provider_price || 0,
        customer_price: viaje.customer_price || 0
      }
      : {}),
    ...(viaje.general_observation
      ? { general_observation: viaje.general_observation }
      : {}),
    has_out_site: viaje.hasOutSite,
    fixed_price_name: viaje.fixedRate?.name || undefined,
    entry_channel: viaje.canalIngreso,
    platform_id: viaje.plataformaId,
    has_priority: viaje.prioridad,
    service_type: viaje.tipoServicio,
    coordination_observation: viaje.observacionCoordinacion,
    reservation_datetime:
      viaje.fechaYHoraDeLlegada.format('YYYY-MM-DD HH:mm') + ':00',
    advance_hours:
      viaje.fechaYHoraDeLlegada.diff(viaje.fechaYHoraDeSalida, 'minutes') / 60,
    hasOutSite: viaje.hasOutSite,
    pay_with_cash: viaje.pay_with_cash,
    requirement: viaje.pay_with_cash
      ? {
        applicant_name: viaje.solicitante.name,
        applicant_email: viaje.solicitante.email,
        applicant_phone: viaje.solicitante.phone
      }
      : {
        applicant_name: viaje.solicitante.name,
        applicant_email: viaje.solicitante.email,
        applicant_phone: viaje.solicitante.phone,
        cost_center: viaje.costCenter,
        sub_cost_center: viaje.subCostCenter,
        sector: viaje.sector,
        authorized_id: viaje.autorizado?.id,
        buy_order: viaje.buyOrder,
        employee_number: viaje.employeeNumber,
        platform_application: viaje.platformApplication,
        trip_number: viaje.tripNumber,
        data_1: viaje.data1,
        data_2: viaje.data2,
        data_3: viaje.data3
      },
    locations: viaje.puntos.map((point) => ({
      address: domicilioBuilder.toBackEnd(point.domicilio),
      observation: point.observacion,
      minutes_waiting: point.minutosEspera,
      passengers: point.pasajeros.map(passangerBuilder.toBackEnd)
    })),
    // Si el tipo de servicio es tipo buses, se agrega el bus_requirement
    ...(viaje.tipoServicio === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES
      ? {
        bus_requirement: { ...viaje.bus_requirement }
      }
      : {}),
    reason_reserve_past: viaje.reason_reserve_past || null,
    airport_details: viaje.airport_details,
    event_id: viaje.event_id,
    ...(viaje.event_name
      ? { event_name: viaje.event_name, is_event: true }
      : {})
  }),
  fromBackEnd: (viaje: any): ViajeTrasladoModelLegacyDontUse => {
    return {
      ...viaje,
      id: viaje.id,
      cliente: viaje.client,
      canalIngreso: viaje.entryChannel,
      plataformaId: viaje.platformId,
      prioridad: viaje.hasPriority,
      solicitante:
        viaje.requirements?.applicant &&
        applicantBuilder.fromBackend(viaje.requirements.applicant),
      tipoServicio: viaje.serviceType,
      fechaYHoraDeSalida: moment(viaje.reservationDatetime).subtract(
        viaje.advanceHours,
        'hours'
      ),
      fechaYHoraDeLlegada: moment(viaje.reservationDatetime),
      observacionCoordinacion: viaje.coordinationObservation,
      estado: viaje.state,
      choferPreAsignado:
        viaje.pre_assigned_driver &&
        choferPartialBuilder.fromBackEnd(viaje.pre_assigned_driver),
      choferAsignado:
        viaje.assigned_driver &&
        choferPartialBuilder.fromBackEnd(viaje.assigned_driver),
      autorizado:
        viaje.requirements?.authorized &&
        authorizedBuilder.fromBackEnd(viaje.requirements.authorized),
      fixedRateName: viaje.fixed_price_name,
      costCenter: viaje.requirements?.costCenter,
      subCostCenter: viaje.requirements?.subCostCenter,
      sector: viaje.requirements?.sector,
      buyOrder: viaje.requirements?.buyOrder,
      employeeNumber: viaje.requirements?.employeeNumber,
      platformApplication: viaje.requirements?.platformApplication,
      tripNumber: viaje.requirements?.tripNumber,
      data1: viaje.requirements?.data1,
      data2: viaje.requirements?.data2,
      data3: viaje.requirements?.data3,
      hasOutSite: viaje.has_out_site,
      puntos: viaje.locations.map(locationBuilder.fromBackEnd),
      state_datetime: moment(viaje.state_datetime),
      is_event: viaje.is_event,
      event_name: viaje.event_name,
      is_llt_pais: viaje?.lltpais,
      provider_price: viaje?.lltpais?.provider_price,
      customer_price: viaje?.lltpais?.customer_price,
      airport_details: viaje.airport_details,
      event: viaje.event ? { id: viaje.event.id, name: viaje.event.name } : null
    };
  }
};
